import { useMemo } from 'react'
import { isToday, isTomorrow, format } from 'date-fns'
import { Tooltip, Typography } from '@mui/material'
import ApartmentIcon from '@mui/icons-material/Apartment'
import VideocamIcon from '@mui/icons-material/Videocam'
import {
  AvatarContainer,
  AvatarImage,
  AvatarBody,
  LanguageContainer,
  Language,
  Name,
  SpecialtyArea,
  AgeRange,
  BackgroundDecorator,
  TitleContainer,
  Slots,
  SlotOption,
} from './styles'
import { languageNames, nameInitials } from '../../utils/helpers'

type AvatarProps = {
  image: string
  displayName: string
  languages: Array<string>
  specialtyAreaNames?: string
  ageRange: string
  videoSlot: Date | null
  officeSlot: Date | null
  onAvatarClick?: () => void
}

/**
 * Builds an appointment URL based on the provided specialty area key and slug.
 * Different subdomains and domains are used depending on the specialty area key.
 *
 * @param {string} specialtyAreaKey - The specialty area key for the appointment.
 * @param {string} slug - The slug to be appended to the generated URL.
 * @returns {string} The constructed appointment URL.
 *
 * Specialty Area Key Domain Examples:
 * - 'primary-urgent-care' -> subdomain: 'schedule', domain: 'perlmanclinic.com'
 * - 'pediatrics'          -> subdomain: 'schedule', domain: 'perlmanpediatrics.com'
 * - 'weight-management'   -> subdomain: 'wellness', domain: 'perlmanclinic.com'
 * - 'mental-health'       -> subdomain: 'mentalhealth', domain: 'perlmanclinic.com'
 */

export const buildApptURL = (
  specialtyAreaKey: string,
  slug: string,
  isVideo?: boolean
): string => {
  let domain = 'perlmanclinic.com'
  let subdomain
  let pocketDocParam = ''

  switch (specialtyAreaKey) {
    case 'primary-urgent-care':
      subdomain = 'schedule'
      if (typeof isVideo !== 'undefined') {
        pocketDocParam = isVideo ? '?is_pocketdoc=true' : '?is_pocketdoc=false'
      }
      break
    case 'pediatrics':
      domain = 'perlmanpediatrics.com'
      subdomain = 'schedule'
      break
    case 'dermatology':
      subdomain = 'dermatology'
      break
    case 'sports-medicine':
      subdomain = 'sportsmedicine'
      break
    case 'lgbtq-health':
      subdomain = 'lgbtq'
      break
    case 'sexual-health':
      subdomain = 'sexual-health'
      break
    case 'weight-management':
    case 'dietitian':
    case 'continuous-glucose-monitoring':
    case 'fertility':
      subdomain = 'wellness'
      break
    case 'mental-health':
    case 'mental-health-therapy':
    case 'psychiatry':
      subdomain = 'mentalhealth'
      break
    case 'endocrinology':
      subdomain = 'endocrinology'
      break
    case 'pain-management':
      subdomain = 'painmanagement'
      break
    default:
      subdomain = 'schedule'
      if (typeof isVideo !== 'undefined') {
        pocketDocParam = isVideo ? '?is_pocketdoc=true' : '?is_pocketdoc=false'
      }
  }

  const providerBaseURL = `https://${subdomain}.${domain}/schedule/provider/`

  return `${providerBaseURL}${slug}${pocketDocParam}`
}

const formatSlot = (slot: Date) => {
  if (isToday(slot)) {
    return format(slot, "'Today at' h:mmaaa")
  }
  if (isTomorrow(slot)) {
    return format(slot, "'Tomorrow at' h:mmaaa")
  }
  return format(slot, 'MMM dd, h:mmaaa')
}

const Avatar = ({
  image,
  displayName,
  languages,
  specialtyAreaNames,
  ageRange,
  videoSlot,
  officeSlot,
  onAvatarClick,
}: AvatarProps): JSX.Element => {
  const formattedVideoSlot = useMemo(() => {
    if (!videoSlot) return null
    return formatSlot(videoSlot)
  }, [videoSlot])

  const formattedOfficeSlot = useMemo(() => {
    if (!officeSlot) return null
    return formatSlot(officeSlot)
  }, [officeSlot])

  return (
    <AvatarContainer elevation={3} onClick={onAvatarClick}>
      <BackgroundDecorator />
      <AvatarBody>
        <AvatarImage src={image} alt={displayName}>
          {nameInitials(displayName)}
        </AvatarImage>
        <TitleContainer>
          <Name color="primary" variant="h4" fontWeight={500}>
            {displayName}
          </Name>
          <SpecialtyArea color="primary" variant="h5" fontWeight={500}>
            {specialtyAreaNames}
          </SpecialtyArea>
          <AgeRange color="primary" variant="h5" fontWeight={500}>
            Ages: {ageRange}
          </AgeRange>
        </TitleContainer>
        <LanguageContainer>
          {languages.map((lang) => (
            <Language
              label={languageNames(lang || '')}
              key={lang}
              variant="outlined"
            />
          ))}
        </LanguageContainer>
        <Slots>
          <Typography fontWeight={500}>Next Available:</Typography>
          <Tooltip title="Video visit" placement="top" arrow>
            <SlotOption available={+Boolean(formattedVideoSlot)}>
              <div>
                <VideocamIcon fontSize="small" />
              </div>
              <Typography fontWeight={600}>
                {formattedVideoSlot || 'Not available'}
              </Typography>
            </SlotOption>
          </Tooltip>
          <Tooltip title="In-Office visit" placement="bottom" arrow>
            <SlotOption available={+Boolean(formattedOfficeSlot)}>
              <div>
                <ApartmentIcon fontSize="small" />
              </div>
              <Typography fontWeight={600}>
                {formattedOfficeSlot || 'Not available'}
              </Typography>
            </SlotOption>
          </Tooltip>
        </Slots>
      </AvatarBody>
    </AvatarContainer>
  )
}

export default Avatar
